import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { Category, PostPage } from '@/types'

import { getRequestOrigin } from '@/lib/request'
import { getCategories, getPostPage } from '@/lib/blog'
import { countLabel, parsePageParam } from '@/lib/format'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { EmptyState } from '@/components/blog/empty-state'
import { LoadError } from '@/components/blog/load-error'
import { PageHeader } from '@/components/blog/page-header'
import { Pagination } from '@/components/blog/pagination'
import { PostGrid } from '@/components/blog/post-grid'
import { TopicChips } from '@/components/blog/topic-chips'

import { POSTS_PER_PAGE } from '@/constants/site'

interface StoriesProps {
    data: PostPage | null
    categories: Category[]
    url: string
}

const Stories: FC<StoriesProps> = ({ data, categories, url }): ReactNode => {
    const page = data?.page ?? 1

    return (
        <Layout>
            <Seo
                title={page > 1 ? `All stories, page ${page}` : 'All stories'}
                description='Every essay and practical guide from the journal, on design, travel, food and culture, newest first.'
                url={url || undefined}
            />

            <PageHeader
                eyebrow='The journal'
                title='All stories'
                description='Essays and practical guides on design, travel, food and culture, newest first.'
                meta={data ? countLabel(data.totalPosts, 'story', 'stories') : undefined}
            >
                <TopicChips categories={categories} activeSlug={null} />
            </PageHeader>

            <Container className='pt-14 sm:pt-16'>
                {!data && <LoadError />}

                {data && data.posts.length === 0 && (
                    <EmptyState
                        title='No stories yet'
                        description='The first essays and guides are being written. Join the newsletter to hear the moment they are published.'
                        action={{ label: 'Join the newsletter', href: '#newsletter', fullReload: true }}
                    />
                )}

                {data && data.posts.length > 0 && (
                    <>
                        <h2 className='sr-only'>
                            Stories
                        </h2>

                        <PostGrid posts={data.posts} headingLevel='h3' />

                        <Pagination page={data.page} totalPages={data.totalPages} basePath='/blog' />
                    </>
                )}
            </Container>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<StoriesProps> = async ({ req, res, query }) => {
    const origin = getRequestOrigin(req)
    const page = parsePageParam(query.page)
    const url = origin ? `${origin}/blog${page > 1 ? `?page=${page}` : ''}` : ''

    try {
        const [data, categories] = await Promise.all([
            getPostPage(page, POSTS_PER_PAGE),
            getCategories()
        ])

        if (page > data.totalPages) return { notFound: true }

        return { props: { data, categories, url } }
    }
    catch (error) {
        console.error('Could not load the stories page:', error)
        res.statusCode = 503

        return { props: { data: null, categories: [], url } }
    }
}

export default Stories
